import React from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import StatCard from '../../components/admin/StatCard';

function AdminDashboard({ subscriptions, onNavigate, toast }) {
  const nonCanceled = subscriptions.filter(s => s.status !== 'canceled');

  // Service status (admin-managed)
  const svcActive    = nonCanceled.filter(s => s.service_status === 'active').length;
  const svcPending   = nonCanceled.filter(s => (s.service_status || 'pending') === 'pending' && s.status !== 'canceled').length;
  const svcSuspended = nonCanceled.filter(s => s.service_status === 'suspended').length;
  const needsUndeploy = subscriptions.filter(s => s.needsUndeploy).length;

  // Stripe billing status
  const stripeActive   = subscriptions.filter(s => s.status === 'active').length;
  const stripeTrialing = subscriptions.filter(s => s.status === 'trialing').length;
  const stripePastDue  = subscriptions.filter(s => s.status === 'past_due').length;
  const stripeAll      = subscriptions.filter(s => s.status !== 'canceled' && s.status !== 'incomplete_expired').length;

  // Subscriptions needing suspension (past_due >= 24h, service still active/pending)
  // Only exclude undeployed — suspended still needs action, cancelled subs are tracked via local DB
  const needsSuspension = subscriptions.filter(s =>
    s.status === 'past_due' &&
    s.hours_past_due >= 24 &&
    s.service_status !== 'undeployed' && s.service_status !== 'suspended'
  );

  return (
    <div>
      <SectionHeader
        title="Dashboard"
        description="Overview of your subscription infrastructure"
      />

      {/* Suspension Required Alert */}
      {needsSuspension.length > 0 && (
        <div className="admin-alert admin-alert--error" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <p style={{ margin: '0 0 6px 0', fontWeight: '700', fontSize: '15px' }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '8px' }} />
                Suspension Required — {needsSuspension.length} subscription{needsSuspension.length !== 1 ? 's' : ''} past_due &gt; 24 hours
              </p>
              <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: 'var(--admin-text-secondary)' }}>
                These subscriptions have had unpaid invoices for over 24 hours. Per our payment notice, service should now be suspended.
              </p>
            </div>
            <button
              className="admin-btn admin-btn--danger admin-btn--sm"
              onClick={() => onNavigate('subscriptions')}
              style={{ flexShrink: 0 }}
            >
              <i className="fa-solid fa-arrow-right" style={{ marginRight: '6px' }} />
              Go to Subscriptions
            </button>
          </div>
          <div className="admin-table-wrapper" style={{ marginTop: '4px' }}>
            <table className="admin-table" style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Domain</th>
                  <th>SRV</th>
                  <th>Hours Overdue</th>
                  <th>Service Status</th>
                </tr>
              </thead>
              <tbody>
                {needsSuspension.map(sub => (
                  <tr
                    key={sub.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onNavigate('subscriptions')}
                    title="Click to open subscriptions"
                  >
                    <td>{sub.customerEmail}</td>
                    <td>{sub.planName || sub.planSlug}</td>
                    <td style={{ color: 'var(--admin-text-secondary)' }}>{sub.domain || '—'}</td>
                    <td>{sub.srv || '—'}</td>
                    <td>
                      <span style={{ color: '#ef4444', fontWeight: '700' }}>
                        {sub.hours_past_due > 0 ? `${sub.hours_past_due}h` : '<1h'}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-badge admin-badge--${sub.service_status === 'active' ? 'active' : 'pending'}`}>
                        {sub.service_status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stripe billing counts */}
      <p style={{ fontSize: '11px', color: 'var(--admin-text-muted)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Stripe Billing Status
      </p>
      <div className="admin-stats-grid" style={{ marginBottom: '24px' }}>
        <StatCard label="All Live" value={stripeAll} icon="fa-solid fa-layer-group" />
        <StatCard label="Stripe Active" value={stripeActive} variant="active" icon="fa-solid fa-circle-check" />
        <StatCard label="Trialing" value={stripeTrialing} variant="info" icon="fa-solid fa-flask" />
        <StatCard label="Past Due" value={stripePastDue} variant={stripePastDue > 0 ? 'danger' : undefined} icon="fa-solid fa-exclamation-triangle" />
      </div>

      {/* Service / deployment counts */}
      <p style={{ fontSize: '11px', color: 'var(--admin-text-muted)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Service Deployment Status
      </p>
      <div className="admin-stats-grid" style={{ marginBottom: '24px' }}>
        <StatCard label="Deployed (Active)" value={svcActive} variant="active" icon="fa-solid fa-circle-check" />
        <StatCard label="Pending Deploy" value={svcPending} variant="pending" icon="fa-solid fa-clock" />
        <StatCard label="Suspended" value={svcSuspended} variant="suspended" icon="fa-solid fa-pause" />
        <StatCard label="Needs Undeploy" value={needsUndeploy} variant={needsUndeploy > 0 ? 'danger' : undefined} icon="fa-solid fa-trash-can" />
      </div>

      <div className="admin-card">
        <h3 className="admin-card-title">Quick Actions</h3>
        <div className="admin-btn-group" style={{ marginTop: 12 }}>
          <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={() => onNavigate('deployments')}>
            <i className="fa-solid fa-rocket" /> Deploy Pending ({svcPending})
          </button>
          <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={() => onNavigate('subscriptions')}>
            <i className="fa-solid fa-credit-card" /> View Subscriptions
          </button>
          <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={() => onNavigate('servers')}>
            <i className="fa-solid fa-server" /> Manage Servers
          </button>
          {stripePastDue > 0 && (
            <button className="admin-btn admin-btn--danger admin-btn--sm" onClick={() => onNavigate('subscriptions')}>
              <i className="fa-solid fa-exclamation-triangle" /> Past Due ({stripePastDue})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
